import events from '@layers/web/constants/events.js'
import type {
  Rbcs,
  UserConsentStatus,
} from '@layers/web/utils/types/tracking.ts'

export const useUserConsent = (reactive?: boolean) => {
  const userConsentStatus = ref(getUserConsentStatus())

  const refreshUserConsentStatus = () => {
    userConsentStatus.value = getUserConsentStatus()
  }

  /**
   * This is where you implement update consent in tags consent modes
   * Runs on each page load, always after default consent but if user
   * haven't answered cookie consent then it shouldn't run until after answering
   */
  const updateTagsConsent = (userConsent: UserConsentStatus) => {
    const boolGrantedOrDenied = (status: boolean) => status ? 'granted' : 'denied'

    const consentModeObj = {
      ad_storage: boolGrantedOrDenied(userConsent.advertising),
      ad_user_data: boolGrantedOrDenied(userConsent.advertising),
      ad_personalization: boolGrantedOrDenied(userConsent.advertising),
      analytics_storage: boolGrantedOrDenied(userConsent.performance),
    }

    gtag('consent', 'update', consentModeObj)

    trackFbq(
      'consent',
      userConsent.advertising ? 'grant' : 'revoke'
    )

    trackClarity(
      'consentv2',
      {
        ad_Storage: boolGrantedOrDenied(userConsent.advertising),
        analytics_Storage: boolGrantedOrDenied(userConsent.performance),
      }
    )

    trackUetq('consent', 'update', consentModeObj)
  }

  /**
   * Set localeStorage rbcs/user consent status
   * Create event that other pixels listen on and enable themselves based on status
   * Update gtag consent mode
   */
  const updateUserConsentStatus = (rbcs: Rbcs, preventUpdateLocalStorage?: boolean) => {
    if (!preventUpdateLocalStorage) {
      trySetLocalStorage('rbcs', JSON.stringify(rbcs))
    }

    const userConsent = transformRbcsToUserConsentStatus(rbcs)

    if (!hasTrackingConsent(userConsent)) {
      clearIdentifiedUserHash()
    }

    updateTagsConsent(userConsent)

    userConsentStatus.value = userConsent
    window.userConsent = userConsent

    // Create event for pixels/trackers to listen on and enable on consent
    window.dispatchEvent(
      new CustomEvent(events.rbcsChange, {
        detail: {
          userConsentStatus: userConsent,
        },
      })
    )

    // Create event that tags in GTM can trigger on
    trackGtag('event', 'consent-update', userConsent)
  }

  if (reactive) {
    onBeforeMount(() => {
      window.addEventListener(events.rbcsChange, refreshUserConsentStatus)
    })

    onBeforeUnmount(() => {
      window.removeEventListener(events.rbcsChange, refreshUserConsentStatus)
    })
  }

  return {
    userConsentStatus,
    updateUserConsentStatus,
  }
}
